import React, { useState, useRef } from 'react';
import { Upload, Search, Sparkles, Clock, ArrowRight } from 'lucide-react';
import { EARTH, TYPOGRAPHY, APP_VERSION } from '../theme/constants.js';
import { CONFIG } from '../config.js';
import { getTrialEmail, saveTrialUser, startTrial } from '../theme/constants.js';
import { track } from '../lib/analytics';
import { SmartGeneratorPanel } from './SmartGeneratorPanel';
import { ReferenceTrackAnalyzer } from './ReferenceTrackAnalyzer';

const SAVED_PROJECTS_KEY = 'arrangementlab_saved_projects';

function getSavedProjects() {
  try {
    const raw = localStorage.getItem(SAVED_PROJECTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function deleteProject(id) {
  const projects = getSavedProjects().filter(p => p.id !== id);
  localStorage.setItem(SAVED_PROJECTS_KEY, JSON.stringify(projects));
}

function LandingPage({ onStartApp, trialExpired, licensed = false }) {
  const [showRefAnalyzer, setShowRefAnalyzer] = useState(false);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [savedProjects, setSavedProjects] = useState(() => getSavedProjects());

  // Email capture
  const hasEmail = !!getTrialEmail();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const emailRef = useRef(null);

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submitEmail = async (action) => {
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
      emailRef.current?.focus();
      return false;
    }
    setEmailError('');
    setSubmitting(true);
    try {
      await fetch('/api/subscribe-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: name || undefined }),
      });
    } catch (_) { /* non-fatal */ }
    track('trial_started');
    saveTrialUser(email, name);
    startTrial();
    setSubmitting(false);
    return true;
  };

  const handleCardClick = async (action) => {
    if (trialExpired) return;
    if (!hasEmail && !getTrialEmail()) {
      // Try to submit whatever's in the form
      if (!email) {
        setEmailError('Please enter your email to start your free trial.');
        emailRef.current?.focus();
        return;
      }
      const ok = await submitEmail(action);
      if (!ok) return;
      action();
    } else {
      action();
    }
  };

  const handleEmailFormSubmit = async (e, action) => {
    e.preventDefault();
    const ok = await submitEmail(action);
    if (ok && action) action();
  };

  const handleDeleteProject = (e, id) => {
    e.stopPropagation();
    deleteProject(id);
    setSavedProjects(getSavedProjects());
  };

  const pathways = [
    {
      icon: Upload,
      title: 'I have a beat or loop',
      desc: 'Upload audio — we\'ll analyze it and suggest a full song structure around it, with tips for every section.',
      bg: `linear-gradient(135deg, #b54a2e, #8f3a28)`,
      action: () => onStartApp('loop'),
    },
    {
      icon: Search,
      title: 'I love this song',
      desc: 'Search any published track. We\'ll reverse-engineer the structure, energy flow, and how to arrange something like it.',
      bg: `linear-gradient(135deg, ${EARTH.forest[600]}, ${EARTH.forest[700]})`,
      action: () => setShowRefAnalyzer(true),
    },
    {
      icon: Sparkles,
      title: 'Just get me started',
      desc: 'Pick a genre or vibe and we\'ll lay out a complete song structure instantly. No audio needed.',
      bg: `linear-gradient(135deg, ${EARTH.stone[600]}, ${EARTH.stone[700]})`,
      action: () => setShowAIGenerator(true),
    },
  ];

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '10px',
    border: `1.5px solid ${EARTH.stone[200]}`,
    backgroundColor: '#fff',
    fontSize: '15px',
    fontFamily: TYPOGRAPHY.body,
    color: EARTH.ink,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: EARTH.cream, fontFamily: TYPOGRAPHY.body, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 40px', borderBottom: `1px solid ${EARTH.stone[100]}` }}>
        <span style={{ fontFamily: TYPOGRAPHY.display, fontSize: '22px', color: '#8f3a28', textTransform: 'lowercase', letterSpacing: '0.5px' }}>
          arrangementlab
        </span>
        {!licensed && (
          <a
            href={CONFIG.CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('checkout_clicked', { location: 'header' })}
            style={{ fontSize: '13px', color: '#555', textDecoration: 'none', fontWeight: 500 }}
          >
            Buy now — ${CONFIG.LAUNCH_PRICE}
          </a>
        )}
      </div>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px' }}>
        <div style={{ width: '100%', maxWidth: '680px' }}>

          {trialExpired ? (
            /* ── Expired state ── */
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h1 style={{ fontFamily: TYPOGRAPHY.display, fontSize: '36px', color: EARTH.ink, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>
                Your trial has ended
              </h1>
              <p style={{ color: '#555', fontSize: '16px', marginBottom: '28px' }}>
                Get lifetime access for a one-time payment — no subscriptions, no recurring charges.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a
                  href={CONFIG.CHECKOUT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track('checkout_clicked', { location: 'trial_expired' })}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '14px 32px', borderRadius: '12px', backgroundColor: EARTH.terracotta[500], color: '#fff', fontWeight: 700, fontSize: '16px', textDecoration: 'none', fontFamily: TYPOGRAPHY.body }}
                >
                  Get Access — ${CONFIG.LAUNCH_PRICE}
                  <ArrowRight style={{ width: '18px', height: '18px' }} />
                </a>
                <button
                  onClick={() => onStartApp('enter-license')}
                  style={{ padding: '14px 28px', borderRadius: '12px', backgroundColor: 'transparent', color: EARTH.stone[600], fontWeight: 600, fontSize: '15px', border: `1.5px solid ${EARTH.stone[300]}`, cursor: 'pointer', fontFamily: TYPOGRAPHY.body }}
                >
                  Enter license key
                </button>
              </div>
              <p style={{ color: '#595959', fontSize: '12px', marginTop: '16px', textAlign: 'center' }}>
                By purchasing, you agree to our{' '}
                <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: '#8f3a28' }}>Terms</a>
                {' '}and{' '}
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#8f3a28' }}>Privacy Policy</a>.
              </p>
            </div>
          ) : (
            <>
              {/* ── Headline ── */}
              <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <h1 style={{ fontFamily: TYPOGRAPHY.display, fontSize: '42px', color: EARTH.ink, textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.1, marginBottom: '12px' }}>
                  You bring the idea.<br />
                  <span style={{ color: '#8f3a28' }}>We'll map the arrangement.</span>
                </h1>
                {!licensed && (
                  <p style={{ color: '#555', fontSize: '16px' }}>
                    {CONFIG.TRIAL_DAYS} days free — no credit card required.
                  </p>
                )}
              </div>

              {/* ── Email capture (shown only if no email yet) ── */}
              {!hasEmail && (
                <div style={{ backgroundColor: EARTH.stone[50], borderRadius: '16px', padding: '28px', marginBottom: '32px', border: `1px solid ${EARTH.stone[200]}` }}>
                  <p style={{ fontWeight: 700, fontSize: '15px', color: EARTH.ink, marginBottom: '16px', textAlign: 'center' }}>
                    Where should we send your trial tips?
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Name <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = EARTH.terracotta[400]}
                        onBlur={(e) => e.target.style.borderColor = EARTH.stone[200]}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Email <span style={{ color: '#8f3a28' }}>*</span>
                      </label>
                      <input
                        ref={emailRef}
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                        style={{ ...inputStyle, borderColor: emailError ? EARTH.terracotta[400] : EARTH.stone[200] }}
                        onFocus={(e) => e.target.style.borderColor = EARTH.terracotta[400]}
                        onBlur={(e) => e.target.style.borderColor = emailError ? EARTH.terracotta[400] : EARTH.stone[200]}
                      />
                    </div>
                  </div>
                  {emailError && (
                    <p style={{ color: '#b54a2e', fontSize: '12px', marginBottom: '8px' }}>{emailError}</p>
                  )}
                  <p style={{ fontSize: '11px', color: '#595959', textAlign: 'center', marginTop: '6px' }}>
                    No spam. Unsubscribe anytime.
                  </p>
                </div>
              )}

              {/* ── Pathway cards ── */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                {pathways.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => handleCardClick(p.action)}
                    disabled={submitting}
                    style={{
                      background: p.bg,
                      color: '#fff',
                      borderRadius: '16px',
                      padding: '32px 24px',
                      textAlign: 'left',
                      border: 'none',
                      cursor: submitting ? 'wait' : 'pointer',
                      transition: 'transform 0.15s, box-shadow 0.15s',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.18)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.12)'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <p.icon style={{ width: '26px', height: '26px', flexShrink: 0 }} />
                      <div style={{ fontFamily: TYPOGRAPHY.display, fontSize: '17px', fontWeight: 700 }}>
                        {p.title}
                      </div>
                    </div>
                    <p style={{ fontSize: '14px', opacity: 0.85, lineHeight: '1.6', margin: 0 }}>
                      {p.desc}
                    </p>
                  </button>
                ))}
              </div>

              {/* ── Docs link ── */}
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <a
                  href="/help"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: '13px', color: '#595959', textDecoration: 'none', fontWeight: 500 }}
                  onMouseEnter={(e) => e.target.style.color = EARTH.terracotta[500]}
                  onMouseLeave={(e) => e.target.style.color = '#595959'}
                >
                  Not sure where to start? Read the docs →
                </a>
              </div>
            </>
          )}

          {/* ── Recent projects ── */}
          {savedProjects.length > 0 && (
            <div style={{ borderRadius: '14px', overflow: 'hidden', border: `1px solid ${EARTH.stone[200]}` }}>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${EARTH.stone[100]}`, backgroundColor: EARTH.stone[50] }}>
                <span style={{ fontFamily: TYPOGRAPHY.display, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#595959' }}>
                  Recent Projects
                </span>
              </div>
              {savedProjects.slice(0, 5).map((project) => (
                <div
                  key={project.id}
                  onClick={() => !trialExpired && onStartApp(project)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${EARTH.stone[100]}`, cursor: trialExpired ? 'not-allowed' : 'pointer', transition: 'background-color 0.1s', opacity: trialExpired ? 0.4 : 1 }}
                  onMouseEnter={(e) => { if (!trialExpired) e.currentTarget.style.backgroundColor = EARTH.stone[50]; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Clock style={{ width: '13px', height: '13px', color: '#595959', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: EARTH.stone[800] }}>{project.name || 'Untitled'}</div>
                      <div style={{ fontSize: '11px', color: '#595959' }}>
                        {project.sections?.length || 0} sections{project.tempo ? ` · ${project.tempo} BPM` : ''}{project.savedAt ? ` · ${new Date(project.savedAt).toLocaleDateString()}` : ''}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDeleteProject(e, project.id)}
                    style={{ background: 'none', border: 'none', color: '#595959', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '2px 6px', borderRadius: '6px' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = EARTH.terracotta[500]; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#595959'; }}
                  >×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '16px', borderTop: `1px solid ${EARTH.stone[100]}` }}>
        <p style={{ fontSize: '11px', color: '#595959' }}>v{APP_VERSION} · Built by BlackCloud Productions</p>
      </div>

      <ReferenceTrackAnalyzer isOpen={showRefAnalyzer} onClose={() => setShowRefAnalyzer(false)} onImport={(structure) => onStartApp(structure)} />
      <SmartGeneratorPanel isOpen={showAIGenerator} onClose={() => setShowAIGenerator(false)} onGenerate={(generated) => onStartApp({ name: `Generated ${generated.genre}`, sections: generated.sections, bars: generated.bars, tempo: generated.tempo })} currentSections={[]} />
    </div>
  );
}

export { LandingPage as StudioLandingPage };
